<div class="final-output-layout">
  <div class="final-output-main">
    <header class="panel-header panel-header-compact">
      <h1>Final Output</h1>
      <p>
        Multi-LLM orchestration generates your professional exercise
        deliverables
      </p>
    </header>

    <!-- Module Generation Error Alert -->
    <div
      class="error-container"
      role="alert"
      aria-live="polite"
      *ngIf="moduleGenerationError"
    >
      <div class="error-banner">
        <span class="error-icon">⚠</span>
        <div class="error-content">
          <p class="error-message">{{ moduleGenerationError }}</p>
          <p class="error-hint">
            Please go back to the Module Plan Review step to resolve this issue.
          </p>
        </div>
        <button type="button" class="error-action-btn" (click)="goToStep(5)">
          Go Back
        </button>
      </div>
    </div>

    <!-- Selected Scenario -->
    <section class="selected-scenario-section" *ngIf="!moduleGenerationError">
      <h2>Selected Scenario</h2>
      <div class="selected-scenario-title">
        {{ selectedScenario?.title ?? "" }}
      </div>
      <div class="selected-scenario-subtitle">
        {{ selectedScenario?.threatType ?? "" }}
      </div>
    </section>

    <!-- Exercise Document Pipeline -->
    <section class="pipeline-section" *ngIf="!moduleGenerationError">
      <h2>Exercise document pipeline</h2>

      <div class="pipeline-step" [class.completed]="documentGenerated">
        <span class="pipeline-step-number">
          <span *ngIf="!documentGenerated">1</span>
          <span *ngIf="documentGenerated" class="checkmark">✓</span>
        </span>
        <span class="pipeline-step-name">Generate Exercise Document</span>
        <span class="pipeline-step-agent">gemini</span>
      </div>

      <button
        type="button"
        class="generate-document-button"
        [class.generate-document-button--loading]="isGeneratingDocument"
        [disabled]="isGeneratingDocument"
        (click)="generateDocument()"
      >
        <span *ngIf="isGeneratingDocument" class="btn-spinner"></span>
        {{
          isGeneratingDocument
            ? "Generating…"
            : documentGenerated
              ? "Regenerate"
              : "Generate Document"
        }}
      </button>

      <!-- Target Impact Level -->
      <div *ngIf="documentGenerated" class="target-impact-section">
        <h3>Target Impact level</h3>
        <div class="impact-level-grid">
          <label
            class="impact-level-option"
            [class.selected]="targetImpactLevel === 'Low'"
          >
            <input
              type="radio"
              name="targetImpactLevel"
              value="Low"
              [(ngModel)]="targetImpactLevel"
            />
            <span class="impact-level-label">Low</span>
          </label>
          <label
            class="impact-level-option"
            [class.selected]="targetImpactLevel === 'Medium'"
          >
            <input
              type="radio"
              name="targetImpactLevel"
              value="Medium"
              [(ngModel)]="targetImpactLevel"
            />
            <span class="impact-level-label">Medium</span>
          </label>
          <label
            class="impact-level-option"
            [class.selected]="targetImpactLevel === 'High'"
          >
            <input
              type="radio"
              name="targetImpactLevel"
              value="High"
              [(ngModel)]="targetImpactLevel"
            />
            <span class="impact-level-label">High</span>
          </label>
          <label
            class="impact-level-option"
            [class.selected]="targetImpactLevel === 'Catastrophic'"
          >
            <input
              type="radio"
              name="targetImpactLevel"
              value="Catastrophic"
              [(ngModel)]="targetImpactLevel"
            />
            <span class="impact-level-label">Catastrophic</span>
          </label>
        </div>
      </div>

      <!-- Red Team Critique -->
      <div *ngIf="documentGenerated" class="red-team-section">
        <h3 class="red-team-title">Red team critique (Optional)</h3>

        <div class="pipeline-step" [class.completed]="redTeamRequested">
          <span class="pipeline-step-number">{{
            redTeamRequested ? "✓" : "2"
          }}</span>
          <span class="pipeline-step-name">Red Team Critique</span>
          <span class="pipeline-step-agent">Ministral</span>
        </div>

        <div class="pipeline-step" [class.completed]="critiqueApplied">
          <span class="pipeline-step-number">{{
            critiqueApplied ? "✓" : "3"
          }}</span>
          <span class="pipeline-step-name">Apply Critique & Refine</span>
          <span class="pipeline-step-agent">Gemini</span>
        </div>

        <button
          *ngIf="!redTeamRequested"
          type="button"
          class="request-review-button"
          [class.request-review-button--loading]="isRequestingRedTeamReview"
          [disabled]="isRequestingRedTeamReview"
          (click)="requestRedTeamReview()"
        >
          <span *ngIf="isRequestingRedTeamReview" class="btn-spinner"></span>
          {{
            isRequestingRedTeamReview
              ? "Requesting review…"
              : "Request Red Team Review"
          }}
        </button>

        <div *ngIf="redTeamRequested" class="red-team-actions">
          <button type="button" class="request-review-button" [disabled]="true">
            View Red Team Critique
          </button>

          <button
            type="button"
            class="request-review-button"
            (click)="applyCritique()"
            [class.request-review-button--loading]="isApplyingCritique"
            [disabled]="critiqueApplied || isApplyingCritique"
          >
            <span *ngIf="isApplyingCritique" class="btn-spinner"></span>
            {{
              isApplyingCritique
                ? "Applying critique…"
                : "Apply Critique & Refine"
            }}
          </button>
        </div>
      </div>

      <!-- Export -->
      <div *ngIf="documentGenerated" class="export-section">
        <h3>Export</h3>
        <button type="button" class="export-word-button">Export Word</button>
      </div>
    </section>

    <!-- PowerPoint Export Pipeline -->
    <section class="pipeline-section" *ngIf="!moduleGenerationError">
      <h2>Powerpoint export pipeline</h2>
      <div class="pipeline-flow">Gemini → Claude → FluidearR</div>

      <div class="pipeline-steps">
        <div class="pipeline-step" [class.completed]="pptPlanGenerated">
          <span class="pipeline-step-number">{{
            pptPlanGenerated ? "✓" : "1"
          }}</span>
          <span class="pipeline-step-name">Plan PowerPoint Presentation</span>
          <span class="pipeline-step-agent">gemini</span>
        </div>

        <div class="pipeline-step" [class.completed]="pptContentGenerated">
          <span class="pipeline-step-number">{{
            pptContentGenerated ? "✓" : "2"
          }}</span>
          <span class="pipeline-step-name">Generate Slide Content</span>
          <span class="pipeline-step-agent">Claude</span>
        </div>

        <div class="pipeline-step" [class.completed]="pptFileGenerated">
          <span class="pipeline-step-number">{{
            pptFileGenerated ? "✓" : "3"
          }}</span>
          <span class="pipeline-step-name">Render PowerPoint File</span>
          <span class="pipeline-step-agent">pptgenjs</span>
        </div>
      </div>

      <button
        *ngIf="!pptPlanGenerated"
        type="button"
        class="generate-powerpoint-button"
        [class.generate-powerpoint-button--loading]="isGeneratingPptPlan"
        [disabled]="isGeneratingPptPlan"
        (click)="generatePowerPointPlan()"
      >
        <span *ngIf="isGeneratingPptPlan" class="btn-spinner"></span>
        {{
          isGeneratingPptPlan
            ? "Generating PowerPoint Plan…"
            : "Generate PowerPoint Plan"
        }}
      </button>

      <button
        *ngIf="pptPlanGenerated"
        type="button"
        class="generate-powerpoint-button"
        [class.generate-powerpoint-button--loading]="
          isGeneratingPptPresentation
        "
        (click)="generatePowerPointPresentation()"
        [disabled]="pptFileGenerated || isGeneratingPptPresentation"
      >
        <span *ngIf="isGeneratingPptPresentation" class="btn-spinner"></span>
        {{
          isGeneratingPptPresentation
            ? "Generating PP Presentation…"
            : pptFileGenerated
              ? "PowerPoint Presentation Generated"
              : "Generate PP Presentation"
        }}
      </button>
    </section>

    <!-- Image Generation -->
    <section class="pipeline-section" *ngIf="!moduleGenerationError">
      <h2>Image Generation</h2>
      <div class="pipeline-subtitle">Gemini 2.1 Flash Image Preview</div>

      <div class="pipeline-step" [class.completed]="crisisImagesGenerated">
        <span class="pipeline-step-number">{{
          crisisImagesGenerated ? "✓" : "1"
        }}</span>
        <span class="pipeline-step-name">Generate Module Image</span>
        <span class="pipeline-step-agent">gemini</span>
      </div>

      <button
        type="button"
        class="generate-images-button"
        [class.generate-images-button--loading]="isGeneratingCrisisImages"
        [disabled]="isGeneratingCrisisImages"
        (click)="generateCrisisImages()"
      >
        <span *ngIf="isGeneratingCrisisImages" class="btn-spinner"></span>
        {{
          isGeneratingCrisisImages
            ? "Generating Crisis Image…"
            : "Generate Crisis Image"
        }}
      </button>
    </section>
  </div>

  <!-- Preview Panel -->
  <div class="final-output-preview" *ngIf="!moduleGenerationError">
    <section class="preview-panel preview-panel-text">
      <div class="preview-panel-header">
        <h3>Text Preview</h3>
        <div class="preview-mode-toggle" *ngIf="documentGenerated">
          <button
            type="button"
            class="preview-mode-btn"
            [class.active]="documentPreviewMode === 'formatted'"
            (click)="setDocumentPreviewMode('formatted')"
          >
            Markdown
          </button>
          <button
            type="button"
            class="preview-mode-btn"
            [class.active]="documentPreviewMode === 'raw'"
            (click)="setDocumentPreviewMode('raw')"
          >
            Raw
          </button>
        </div>
      </div>

      <div class="preview-panel-body preview-panel-body-text">
        <div class="preview-skeleton" *ngIf="isGeneratingDocument">
          <div class="preview-skeleton-line preview-skeleton-line-title"></div>
          <div class="preview-skeleton-line"></div>
          <div class="preview-skeleton-line"></div>
          <div class="preview-skeleton-line preview-skeleton-line-short"></div>
          <div class="preview-skeleton-line"></div>
          <div class="preview-skeleton-line"></div>
          <div class="preview-skeleton-line preview-skeleton-line-medium"></div>
          <div class="preview-skeleton-line"></div>
        </div>

        <p
          class="preview-empty"
          *ngIf="
            !documentGenerated &&
            !isGeneratingDocument &&
            !documentGenerationError
          "
        >
          A document preview has not yet been created
        </p>

        <div class="error-container" *ngIf="documentGenerationError">
          <div class="error-banner">
            <span class="error-icon">⚠</span>
            <div class="error-content">
              <p class="error-message">
                {{ documentGenerationError }}
              </p>
              <button
                type="button"
                class="error-retry-btn"
                (click)="generateDocument()"
              >
                Retry Document Generation
              </button>
            </div>
          </div>
        </div>

        <div
          class="preview-document"
          *ngIf="documentGenerated && !documentGenerationError"
        >
          <div
            class="preview-markdown"
            *ngIf="documentPreviewMode === 'formatted'; else rawPreviewMode"
            [innerHTML]="documentPreviewRenderedHtml"
          ></div>
          <ng-template #rawPreviewMode>
            <pre class="preview-document-raw">{{ documentPreviewContent }}</pre>
          </ng-template>
        </div>

        <section
          class="preview-critique"
          *ngIf="redTeamRequested && redTeamCritiqueContent && !redTeamError"
        >
          <div class="preview-critique-header">
            <h4>Red Team Critique</h4>
            <span>Ministral</span>
          </div>

          <div class="preview-critique-body">
            <p>{{ redTeamCritiqueContent }}</p>
          </div>
        </section>

        <div class="error-container" *ngIf="redTeamError">
          <div class="error-banner">
            <span class="error-icon">⚠</span>
            <div class="error-content">
              <p class="error-message">{{ redTeamError }}</p>
              <button
                type="button"
                class="error-retry-btn"
                (click)="requestRedTeamReview()"
              >
                Retry Red Team Review
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="preview-panel preview-panel-ppt">
      <div class="preview-panel-header">
        <h3>PowerPoint Preview</h3>
        <div class="preview-panel-header-right">
          <span>{{ pptContentSlides.length }} slides</span>
          <button
            *ngIf="pptContentSlides.length > 0"
            class="ppt-download-btn"
            (click)="downloadPptPresentation()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download .pptx
          </button>
        </div>
      </div>

      <div class="preview-panel-body">
        <div class="error-container" *ngIf="pptPlanError">
          <div class="error-banner">
            <span class="error-icon">⚠</span>
            <div class="error-content">
              <p class="error-message">
                {{ pptPlanError }}
              </p>
              <button
                type="button"
                class="error-retry-btn"
                (click)="generatePowerPointPlan()"
              >
                Retry PowerPoint Plan Generation
              </button>
            </div>
          </div>
        </div>

        <div
          class="preview-skeleton preview-skeleton--ppt"
          *ngIf="isGeneratingPptPresentation"
        >
          <div class="preview-skeleton-line preview-skeleton-line-title"></div>
          <div class="preview-skeleton-line"></div>
          <div class="preview-skeleton-line preview-skeleton-line-medium"></div>
          <div class="preview-skeleton-line"></div>
          <div class="preview-skeleton-line preview-skeleton-line-short"></div>
          <div class="preview-skeleton-thumbnails">
            <div class="preview-skeleton-thumbnail"></div>
            <div class="preview-skeleton-thumbnail"></div>
            <div class="preview-skeleton-thumbnail"></div>
          </div>
        </div>

        <p
          class="preview-empty"
          *ngIf="
            pptContentSlides.length === 0 &&
            !isGeneratingPptPresentation &&
            !pptPresentationError
          "
        >
          Slides have not been generated yet
        </p>

        <div class="error-container" *ngIf="pptPresentationError">
          <div class="error-banner">
            <span class="error-icon">⚠</span>
            <div class="error-content">
              <p class="error-message">
                {{ pptPresentationError }}
              </p>
              <button
                type="button"
                class="error-retry-btn"
                (click)="generatePowerPointPresentation()"
              >
                Retry PowerPoint Generation
              </button>
            </div>
          </div>
        </div>

        <div
          class="ppt-viewer"
          *ngIf="pptContentSlides.length > 0 && !isGeneratingPptPresentation"
        >
          <div class="ppt-viewer-toolbar">
            <button
              type="button"
              class="ppt-nav-btn"
              (click)="goToPreviousPptSlide()"
              [disabled]="currentPptPreviewSlideIndex === 0"
            >
              Previous
            </button>
            <span class="ppt-counter">
              Slide {{ currentPptPreviewSlideIndex + 1 }} of
              {{ pptContentSlides.length }}
            </span>
            <button
              type="button"
              class="ppt-nav-btn"
              (click)="goToNextPptSlide()"
              [disabled]="
                currentPptPreviewSlideIndex >= pptContentSlides.length - 1
              "
            >
              Next
            </button>
          </div>

          <article
            *ngIf="activePptSlide as slide"
            [class]="'preview-ppt-card preview-ppt-card--' + slide.layout"
          >
            <div class="preview-ppt-meta">
              <span class="preview-ppt-number"
                >Slide {{ slide.slideNumber }}</span
              >
              <span class="preview-ppt-layout">{{ slide.layout }}</span>
            </div>

            <ng-container *ngIf="slide.layout === 'title'">
              <div class="slide-preview slide-preview--title">
                <p class="slide-headline">
                  {{ $any(slide.content).headline || slide.title }}
                </p>
              </div>
            </ng-container>

            <ng-container *ngIf="slide.layout === 'module-intro'">
              <div class="slide-preview slide-preview--module">
                <span
                  class="slide-module-badge"
                  *ngIf="$any(slide.content).moduleNumber"
                >
                  MODULE {{ $any(slide.content).moduleNumber }}
                </span>
                <h5 class="slide-title">{{ slide.title }}</h5>
                <span
                  class="slide-timescale-badge"
                  *ngIf="$any(slide.content).timescale"
                >
                  {{ $any(slide.content).timescale }}
                </span>
                <p class="slide-summary" *ngIf="$any(slide.content).summary">
                  {{ $any(slide.content).summary }}
                </p>
              </div>
            </ng-container>

            <ng-container *ngIf="slide.layout === 'two-column'">
              <h5 class="slide-title">{{ slide.title }}</h5>
              <div class="slide-preview slide-preview--two-col">
                <ul
                  class="slide-col"
                  *ngIf="$any(slide.content).leftColumn?.length"
                >
                  <li *ngFor="let item of $any(slide.content).leftColumn">
                    {{ item }}
                  </li>
                </ul>
                <ul
                  class="slide-col"
                  *ngIf="$any(slide.content).rightColumn?.length"
                >
                  <li *ngFor="let item of $any(slide.content).rightColumn">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </ng-container>

            <ng-container *ngIf="slide.layout === 'timeline'">
              <h5 class="slide-title">{{ slide.title }}</h5>
              <ul
                class="slide-timeline"
                *ngIf="$any(slide.content).timelineItems?.length"
              >
                <li
                  *ngFor="let item of $any(slide.content).timelineItems"
                  class="slide-timeline-item"
                >
                  <span class="slide-timeline-time">{{ item.time }}</span>
                  <span class="slide-timeline-event">{{ item.event }}</span>
                </li>
              </ul>
            </ng-container>

            <ng-container *ngIf="slide.layout === 'questions'">
              <h5 class="slide-title">{{ slide.title }}</h5>
              <ol
                class="slide-questions"
                *ngIf="$any(slide.content).bullets?.length"
              >
                <li *ngFor="let q of $any(slide.content).bullets">
                  {{ q }}
                </li>
              </ol>
            </ng-container>

            <ng-container *ngIf="slide.layout === 'aar'">
              <h5 class="slide-title">{{ slide.title }}</h5>
              <div class="slide-preview slide-preview--aar">
                <div
                  class="slide-aar-col slide-aar-col--good"
                  *ngIf="$any(slide.content).whatWorked?.length"
                >
                  <span>What Worked</span>
                  <ul>
                    <li *ngFor="let item of $any(slide.content).whatWorked">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                <div
                  class="slide-aar-col slide-aar-col--bad"
                  *ngIf="$any(slide.content).whatDidntWork?.length"
                >
                  <span>What Didn't Work</span>
                  <ul>
                    <li *ngFor="let item of $any(slide.content).whatDidntWork">
                      {{ item }}
                    </li>
                  </ul>
                </div>
                <div
                  class="slide-aar-col slide-aar-col--improve"
                  *ngIf="$any(slide.content).improvements?.length"
                >
                  <span>Improvements</span>
                  <ul>
                    <li *ngFor="let item of $any(slide.content).improvements">
                      {{ item }}
                    </li>
                  </ul>
                </div>
              </div>
            </ng-container>

            <div class="preview-ppt-meta">
              <ng-container
                *ngIf="
                  slide.layout !== 'title' &&
                  slide.layout !== 'module-intro' &&
                  slide.layout !== 'two-column' &&
                  slide.layout !== 'timeline' &&
                  slide.layout !== 'questions' &&
                  slide.layout !== 'aar'
                "
              >
                <h5 class="slide-title">{{ slide.title }}</h5>
                <ul
                  class="slide-bullets"
                  *ngIf="$any(slide.content).bullets?.length"
                >
                  <li *ngFor="let b of $any(slide.content).bullets">
                    {{ b }}
                  </li>
                </ul>
                <ul
                  class="slide-bullets"
                  *ngIf="
                    !$any(slide.content).bullets &&
                    $any(slide.content).items?.length
                  "
                >
                  <li *ngFor="let b of $any(slide.content).items">
                    {{ b }}
                  </li>
                </ul>
              </ng-container>
            </div>

            <p *ngIf="slide.speakerNotes" class="preview-ppt-notes">
              {{ slide.speakerNotes }}
            </p>
          </article>

          <div class="ppt-thumbnail-strip">
            <button
              type="button"
              class="ppt-thumbnail"
              *ngFor="let slide of pptContentSlides; let i = index"
              [class.active]="i === currentPptPreviewSlideIndex"
              (click)="selectPptSlide(i)"
            >
              <span>Slide {{ i + 1 }}</span>
              <small>{{ slide.title || slide.layout }}</small>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="preview-panel preview-panel-images">
      <div class="preview-panel-header">
        <h3>Image Preview</h3>
        <span>{{ crisisImagePreviews.length }} images</span>
      </div>

      <div class="preview-panel-body">
        <div
          class="preview-skeleton preview-skeleton--images"
          *ngIf="isGeneratingCrisisImages"
        >
          <div class="preview-skeleton-line preview-skeleton-line-title"></div>
          <div class="preview-skeleton-image-grid">
            <div class="preview-skeleton-image-card"></div>
            <div class="preview-skeleton-image-card"></div>
            <div class="preview-skeleton-image-card"></div>
          </div>
        </div>

        <div class="error-container" *ngIf="crisisImagesError">
          <div class="error-banner">
            <span class="error-icon">⚠</span>
            <div class="error-content">
              <p class="error-message">{{ crisisImagesError }}</p>
              <button
                type="button"
                class="error-retry-btn"
                (click)="generateCrisisImages()"
              >
                Retry Crisis Image Generation
              </button>
            </div>
          </div>
        </div>

        <p
          class="preview-empty"
          *ngIf="
            crisisImagePreviews.length === 0 &&
            !isGeneratingCrisisImages &&
            !crisisImagesError
          "
        >
          Images have not been generated yet
        </p>

        <div
          class="preview-images"
          *ngIf="crisisImagePreviews.length > 0 && !isGeneratingCrisisImages"
        >
          <div class="preview-images-header">
            <h4>Generated Crisis Images</h4>
            <span>Download Ready</span>
          </div>

          <div class="preview-image-list">
            <article
              class="image-preview-card"
              *ngFor="let preview of crisisImagePreviews"
            >
              <img [src]="preview.src" [alt]="preview.title" />
              <div class="image-preview-meta">
                <span>{{ preview.title }}</span>
                <button
                  type="button"
                  class="request-review-button"
                  (click)="downloadCrisisImage(preview)"
                >
                  Download Image
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  </div>
</div>
